import { ColumnSeverityContext } from "features/CenterContent/RoleContent/AlternativeTransport/MeasureOverview/ColumnSeverityContext";
import { MeasureCardContent } from "features/CenterContent/RoleContent/AlternativeTransport/MeasureOverview/MeasureOverviewColumn/MeasureCard/MeasureCardContent";
import { MeasureCardWrapper } from "features/CenterContent/RoleContent/AlternativeTransport/MeasureOverview/MeasureOverviewColumn/MeasureCard/MeasureCardWrapper";
import { getSemanticsForMeasureCard } from "features/CenterContent/RoleContent/AlternativeTransport/MeasureOverview/MeasureOverviewColumn/MeasureCard/semanticFunctions";
import { SemanticStaticCard } from "features/CenterContent/RoleContent/AlternativeTransport/MeasureOverview/MeasureOverviewColumn/MeasureCard/SemanticStaticCard";
import { useMeasureCardMuteHistory } from "features/CenterContent/RoleContent/AlternativeTransport/MeasureOverview/MeasureOverviewColumn/MeasureCard/useMeasureCardMuteHistory";
import { usePreviousEventVersion } from "features/CenterContent/RoleContent/AlternativeTransport/MeasureOverview/MeasureOverviewColumn/MeasureCard/usePreviousEventVersion";
import { useRecentlyUpdatedState } from "features/CenterContent/RoleContent/AlternativeTransport/MeasureOverview/MeasureOverviewColumn/MeasureCard/useRecentlyUpdatedState";
import {
  PlanEventInformation,
  PlanMeasureKey,
} from "features/CenterContent/RoleContent/AlternativeTransport/MeasureOverview/types";
import { eventIsInfrastructureEvent } from "features/History/utils/utils";
import { FC, useCallback, useContext, useEffect } from "react";
import { generatePath, useNavigate } from "react-router-dom";
import { ALTERNATIVE_TRANSPORT_DECISION_SUPPORT } from "shared/utils/routes";

type MeasureCardProps = {
  measureColumnKey: PlanMeasureKey;
  measure: PlanEventInformation;
};

export const MeasureCard: FC<MeasureCardProps> = ({
  measureColumnKey,
  measure,
}) => {
  const navigate = useNavigate();
  const { dispatch } = useContext(ColumnSeverityContext);

  const { information: currentInformation } = measure;
  const { data: previousMeasureVersion } =
    usePreviousEventVersion(currentInformation);

  const { isMuted, muteHistory, addMuteHistoryEntry } =
    useMeasureCardMuteHistory(
      currentInformation.uuid,
      currentInformation.state,
    );

  const { recentlyUpdated, acknowledgeLatestVersion } = useRecentlyUpdatedState(
    currentInformation,
    previousMeasureVersion && previousMeasureVersion.information, // Preserve explicit null (404/no previous version exists)
  );

  const semantics = getSemanticsForMeasureCard(
    recentlyUpdated,
    isMuted,
    measureColumnKey,
  );

  const isInteractable = eventIsInfrastructureEvent(currentInformation);

  const onClick = useCallback(() => {
    if (isInteractable) {
      navigate(
        generatePath(ALTERNATIVE_TRANSPORT_DECISION_SUPPORT, {
          uuid: currentInformation.uuid,
        }),
      );
    }
  }, [isInteractable, currentInformation.uuid]);

  useEffect(() => {
    // Register the severity of this measure in the column context, so we can colour notifications correctly
    const { group } = measure.information.type;
    if (group === "STATION" || group === "GLOBAL") return () => {};

    const eventGroup = group === "INFRASTRUCTURE" ? "infrastructure" : "train";

    const severity = semantics === "neutral" ? "transparent" : semantics;

    dispatch({
      type: "add",
      column: measureColumnKey,
      eventGroup,
      severity,
    });

    return () => {
      // Unregister on rerender so that we don't get duplicates or outdated values
      dispatch({
        type: "remove",
        column: measureColumnKey,
        eventGroup,
        severity,
      });
    };
  }, [semantics, measureColumnKey, measure.information.type]);

  return (
    <MeasureCardWrapper>
      <SemanticStaticCard
        semantics={semantics}
        px={2}
        py={1.5}
        borderRadius="xs"
        cursor={isInteractable ? "pointer" : undefined}
        onClick={onClick}
      >
        <MeasureCardContent
          measure={measure}
          previousMeasure={previousMeasureVersion}
          isMuted={isMuted}
          semantics={semantics}
          muteHistory={muteHistory}
          addMuteHistoryEntry={addMuteHistoryEntry}
          measureKey={measureColumnKey}
          recentlyUpdated={recentlyUpdated}
          acknowledgeLatestVersion={acknowledgeLatestVersion}
        />
      </SemanticStaticCard>
    </MeasureCardWrapper>
  );
};
